import type { SpecimenLogResponse } from "../../services/reports.service";
import {
  billingAccountTypeLabel,
  billingDateBasisLabel,
} from "./billingReportLabels";
import styles from "./BillingReportPrint.module.css";

interface SpecimenLogPrintReportProps {
  report: SpecimenLogResponse;
}

export function SpecimenLogPrintReport({
  report,
}: SpecimenLogPrintReportProps) {
  return (
    <main className={styles.page} data-billing-report-document="true">
      <h1 className={styles.title}>Specimen Log Report</h1>
      <section className={styles.metadata}>
        <p className={styles.metadataLine}>
          Generated At: {report.generatedAt}
        </p>
        <p className={styles.metadataLine}>
          Filtered By: {billingDateBasisLabel(report.dateBasis)}
        </p>
        <p className={styles.metadataLine}>
          From: {report.startDate} - {report.endDate}
        </p>
        <p className={styles.metadataLine}>
          Account Type:{" "}
          {report.accountTypes.map(billingAccountTypeLabel).join(", ")}
        </p>
        {report.activeFilters.map((filter) => (
          <p className={styles.metadataLine} key={filter.label}>
            {filter.label}: {filter.value}
          </p>
        ))}
      </section>

      {report.unassignedCaseCount > 0 && (
        <p className={styles.warning}>
          RECONCILIATION REQUIRED: {report.unassignedCaseCount} qualifying
          case(s) have an unassigned billing account.
        </p>
      )}
      {report.reconciliationCases.length > 0 && (
        <table className={`${styles.table} ${styles.reviewTable}`}>
          <thead>
            <tr>
              <th>Review Case</th>
              <th>Issue</th>
            </tr>
          </thead>
          <tbody>
            {report.reconciliationCases.flatMap((reportCase) =>
              reportCase.issues.map((issue) => (
                <tr key={`${reportCase.caseId}:${issue}`}>
                  <td>{reportCase.caseNumber}</td>
                  <td>{issue}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Account Type</th>
            <th>Primary Account Name</th>
            <th className={styles.number}>Case Count</th>
            <th className={styles.number}>Part Count</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row) => (
            <tr key={`${row.accountType}:${row.accountId}`}>
              <td>{billingAccountTypeLabel(row.accountType)}</td>
              <td>{row.accountName}</td>
              <td className={styles.number}>{row.caseCount}</td>
              <td className={styles.number}>{row.partCount}</td>
            </tr>
          ))}
          <tr className={styles.total}>
            <td>All account types</td>
            <td>Totals</td>
            <td className={styles.number}>{report.totalCases}</td>
            <td className={styles.number}>{report.totalParts}</td>
          </tr>
        </tbody>
      </table>
    </main>
  );
}
